import axios from "axios";
import { useEffect, useState } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import DatePicker from "react-datepicker";
import { useSearchParams } from "react-router-dom";
import { API_CONSTANTS } from "../../common/constants/api";
import { IBusLocation } from "../../common/types";
import { SESSION_KEYS } from "../../common/constants/sessionKeys";
import styles from "./Home.module.scss";
import { Col, Row } from "react-bootstrap";
import { HeightIcon, WidthIcon } from "@radix-ui/react-icons";

const Home = () => {
  const [searchParams] = useSearchParams();
  const [fromLocation, setFromLocation] = useState<IBusLocation[]>([]);
  const [toLocation, setToLocation] = useState<IBusLocation[]>([]);
  const [jDate, setJDate] = useState<Date | null>(null);
  const [allLocations, setAllLocations] = useState<IBusLocation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const clearSearch = () => {
    setFromLocation([]);
    setToLocation([]);
    setJDate(null);

    sessionStorage.removeItem(SESSION_KEYS.FROM_LOCATION);
    sessionStorage.removeItem(SESSION_KEYS.FROM_LOCATION_CODE);
    sessionStorage.removeItem(SESSION_KEYS.TO_LOCATION);
    sessionStorage.removeItem(SESSION_KEYS.TO_LOCATION_CODE);
  };

  const getFormattedTime = (d: Date | null) => {
    if (!d) {
      return "";
    }
    return `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${(
      "0" + d.getDate()
    ).slice(-2)}`;
  };

  const dateOnly = (d: Date) => {
    const next = new Date(d);
    next.setHours(0, 0, 0, 0);
    return next;
  };

  const today = dateOnly(new Date());
  const tomorrow = dateOnly(new Date(today.getTime() + 24 * 60 * 60 * 1000));
  const selectedDay = jDate ? getFormattedTime(dateOnly(jDate)) : "";

  const swapLocationsHandler = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    const _fromLocation = fromLocation;
    const _toLocation = toLocation;
    setFromLocation(_toLocation);
    setToLocation(_fromLocation);

    if (_toLocation[0]) {
      sessionStorage.setItem(
        SESSION_KEYS.FROM_LOCATION_CODE,
        `${_toLocation[0]?.busStopId}`
      );
      sessionStorage.setItem(
        SESSION_KEYS.FROM_LOCATION,
        `${_toLocation[0]?.busStopName}`
      );
    } else {
      sessionStorage.removeItem(SESSION_KEYS.FROM_LOCATION);
      sessionStorage.removeItem(SESSION_KEYS.FROM_LOCATION_CODE);
    }

    if (_fromLocation[0]) {
      sessionStorage.setItem(
        SESSION_KEYS.TO_LOCATION_CODE,
        `${_fromLocation[0]?.busStopId}`
      );
      sessionStorage.setItem(
        SESSION_KEYS.TO_LOCATION,
        `${_fromLocation[0]?.busStopName}`
      );
    } else {
      sessionStorage.removeItem(SESSION_KEYS.TO_LOCATION);
      sessionStorage.removeItem(SESSION_KEYS.TO_LOCATION_CODE);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    axios
      .get(API_CONSTANTS.BUS_LOCATIONS)
      .then((res) => {
        setAllLocations(res.data);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load bus locations", err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (allLocations.length > 0) {
      const _fromLoc = searchParams.get("from");
      const _toLoc = searchParams.get("to");
      const _jDate = searchParams.get("date");
      if (_fromLoc && _toLoc && _jDate) {
        const _fromLocation: IBusLocation | undefined = allLocations.find(
          (loc) => loc.busStopId === parseInt(_fromLoc)
        );
        const _toLocation: IBusLocation | undefined = allLocations.find(
          (loc) => loc.busStopId === parseInt(_toLoc)
        );
        if (_fromLocation) {
          setFromLocation([_fromLocation]);
        }
        if (_toLocation) {
          setToLocation([_toLocation]);
        }
        setJDate(new Date(_jDate));
      }
    }
  }, [searchParams, allLocations]);

  const canSearch =
    fromLocation?.[0]?.busStopId &&
    toLocation?.[0]?.busStopId &&
    jDate;

  return (
    <div className={`container ${styles.HomeContainer}`}>
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>Book Your Bus Journey</h1>
        <p className={styles.heroSubtitle}>
          Search routes, compare prices, and reserve your seat in seconds.
          Travel made simple with QuickBusRide.
        </p>
      </section>

      {isLoading ? (
        <div className="d-flex justify-content-center my-5" role="status">
          <div className="spinner-border text-primary" aria-hidden="true" />
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : (
        <form>
          <div className={styles.searchCard}>
            <div className={styles.searchCardTitle}>
              <span>🔍</span> Search Buses
            </div>
            <Row className="g-3 align-items-end">
              <Col xs={12} lg={3} md={5}>
                <label htmlFor="fromLocation" className={styles.formItemLabel}>
                  From
                </label>
                <Typeahead
                  id="fromLocation"
                  onChange={(_selected) => {
                    setFromLocation(_selected as IBusLocation[]);
                    sessionStorage.setItem(
                      SESSION_KEYS.FROM_LOCATION_CODE,
                      `${(_selected as IBusLocation[])[0]?.busStopId}`
                    );
                    sessionStorage.setItem(
                      SESSION_KEYS.FROM_LOCATION,
                      `${(_selected as IBusLocation[])[0]?.busStopName}`
                    );
                  }}
                  options={allLocations}
                  labelKey={"busStopName"}
                  placeholder="Select departure city"
                  aria-label="fromLocation"
                  selected={fromLocation}
                  className={styles.typeahead}
                />
              </Col>

              <Col xs={12} lg={1} md={1} className={styles.formIconContainer}>
                <button
                  type="button"
                  className={styles.swapButton}
                  onClick={swapLocationsHandler}
                  aria-label="Swap locations"
                >
                  <WidthIcon
                    width="18"
                    height="18"
                    className={styles.widthIcon}
                  />
                  <HeightIcon
                    width="18"
                    height="18"
                    className={styles.heightIcon}
                  />
                </button>
              </Col>

              <Col xs={12} lg={3} md={5}>
                <label htmlFor="toLocation" className={styles.formItemLabel}>
                  To
                </label>
                <Typeahead
                  id="toLocation"
                  onChange={(_selected) => {
                    sessionStorage.setItem(
                      SESSION_KEYS.TO_LOCATION_CODE,
                      `${(_selected as IBusLocation[])[0]?.busStopId}`
                    );
                    sessionStorage.setItem(
                      SESSION_KEYS.TO_LOCATION,
                      `${(_selected as IBusLocation[])[0]?.busStopName}`
                    );
                    setToLocation(_selected as IBusLocation[]);
                  }}
                  options={allLocations}
                  placeholder="Select destination city"
                  labelKey={"busStopName"}
                  aria-label="toLocation"
                  selected={toLocation}
                  className={styles.typeahead}
                />
              </Col>

              <Col xs={12} lg={5} md={12}>
                <label htmlFor="journeyDate" className={styles.formItemLabel}>
                  Date of Journey
                </label>
                <div className={styles.dateRow}>
                  <DatePicker
                    selected={jDate}
                    onChange={(e) => setJDate(e)}
                    className={`form-control ${styles.datePicker}`}
                    name="journeyDate"
                    placeholderText="Select date"
                    aria-label="journey"
                    id="journeyDate"
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                  />
                  <button
                    type="button"
                    className={selectedDay === getFormattedTime(today) ? styles.dateChoiceActive : styles.dateChoice}
                    onClick={() => setJDate(today)}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    className={selectedDay === getFormattedTime(tomorrow) ? styles.dateChoiceActive : styles.dateChoice}
                    onClick={() => setJDate(tomorrow)}
                  >
                    Tomorrow
                  </button>
                </div>
              </Col>

              <Col xs={12}>
                <div className={styles.actionButtons}>
                  <a
                    className={`btn btn-primary btn-lg ${styles.searchBtn} ${
                      !canSearch ? "disabled" : ""
                    }`}
                    href={`/buses?from=${fromLocation?.[0]?.busStopId}&to=${
                      toLocation?.[0]?.busStopId
                    }&date=${getFormattedTime(jDate)}`}
                  >
                    Search Buses
                  </a>
                  <button
                    type="button"
                    disabled={
                      !fromLocation?.[0] && !toLocation?.[0] && !jDate
                    }
                    className={`btn btn-outline-primary btn-lg ${styles.clearBtn}`}
                    onClick={clearSearch}
                  >
                    Clear
                  </button>
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.features}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>⚡</div>
              <div className={styles.featureTitle}>Instant Booking</div>
              <div className={styles.featureDesc}>
                Book tickets in seconds with our fast checkout process
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>💺</div>
              <div className={styles.featureTitle}>Choose Your Seat</div>
              <div className={styles.featureDesc}>
                Interactive seat map to pick your preferred spot
              </div>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>🔒</div>
              <div className={styles.featureTitle}>Secure Payment</div>
              <div className={styles.featureDesc}>
                Safe and encrypted payment via trusted gateways
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Home;
